import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { DataSources } from '~src/datasource/resources/Datasources';
import { DetailsTooltipStyles } from '~src/spacesPlatform/components/card/DetailsTooltipStyles';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';
import { ConnectionDisplay } from '~src/spacesPlatform/components/ListView/DataConnectionComponents';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { EntityLinkProps } from '~src/spacesPlatform/components/tooltip/EntityLink';
import { LocationField } from '~src/spacesPlatform/components/tooltip/LocationField';
import { Name } from '~src/spacesPlatform/components/tooltip/Name';
import { RelatedEntity } from '~src/spacesPlatform/components/tooltip/RelatedEntity';
import { RelatedEntityList } from '~src/spacesPlatform/components/tooltip/RelatedEntityList';
import { Tooltip } from '~src/spacesPlatform/components/tooltip/Tooltip';
import { ContentTooltipProps, getPersonalSpaceLink, getProjectLink, TooltipDescriptionTestId } from '~src/spacesPlatform/components/tooltip/TooltipHelper';
import { User } from '~src/spacesPlatform/components/tooltip/User';
import { Tags } from '~src/tags/components/tooltips/Tags';

import { Messages } from '../../../codegen/Localize';
import { IContentInfo } from '../../../spacesPlatform/models/IContentInfo';
import { UserMapper } from '../../../userManagement/users/models/mappers/UserMapper';
import { IDataConnectionInfo } from '../../dataConnection/models/IDataConnectionInfo';
import { IDataSourceInfo } from '../../models/IDataSourceInfo';
import { getCommonDatasourceInfo } from '../../models/mappers/CommonDatasourceInfo';

export function DatasourceTooltip(props: ContentTooltipProps): JSX.Element {
  const relatedConnections = getRelatedConnectionLinkProps(props.content, props.urls);
  const extraDetail = getCommonDatasourceInfo(props.content);
  const certDetails = extraDetail ? (extraDetail.certificationDetails || null) : null;
  const hasCertDetails = certDetails;
  const isPublished = DataSources.isPublished(extraDetail);
  const isEmbedded = DataSources.isEmbedded(extraDetail);

  const workbookLink: EntityLinkProps = extraDetail.workbook ? {
    href: props.urls.workbookUrl(extraDetail.workbook.id),
    iconImagePath: imgPath('workbook_content_type_small.svg'),
    iconHeightOverride: 18,  // the icon height is 18 just for workbooks :|
    name: extraDetail.workbook.name
  } : null;

  const projectLink = getProjectLink((props.content as IDataSourceInfo).project, props.urls);
  const personalSpaceLink = getPersonalSpaceLink(props.urls);
  const locationType = (props.content as IDataSourceInfo).location?.type;
  const ownerId = props.content.owner?.id;

  return (
    <Tooltip>
      <Name name={props.content.name} />
      <MarkupField markup={props.content.description}
                   testId={TooltipDescriptionTestId}
                   extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]}/>
      <dl className={DetailsTooltipStyles.DescriptionList}>
        <Tags tags={props.content.tags}/>
        <RelatedEntityList label={Messages.column_connects_to()} list={relatedConnections}/>
        {isEmbedded && <RelatedEntity label={Messages.datasourceCardWorkbook()} link={workbookLink}/>}
        {(props.content as IDataSourceInfo).luid && <>
          <dt className={DetailsTooltipStyles.DescriptionTerm}>{Messages.luid()}</dt>
          <dd className={DetailsTooltipStyles.DescriptionDetails} style={{wordBreak: 'break-all'}}>
            {(props.content as IDataSourceInfo).luid}
          </dd>
        </>}
        <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace}
                       locationType={locationType} projectLink={projectLink} personalSpaceLink={personalSpaceLink}
                       contentOwnerId={ownerId}/>
        {isPublished && <User user={props.content.owner} label={Messages.owner()} urls={props.urls}/>}
        {FeatureFlags.modifiedByUserEnabled && isPublished && props.content.modifiedBy !== undefined &&
          <User user={props.content.modifiedBy} label={Messages.column_modified_by()} urls={props.urls}/>}
      </dl>
      {hasCertDetails && hasCertDetails.isCertified && (
        <div
          className={`${DetailsTooltipStyles.MetadataSection} ${DetailsTooltipStyles.MetadataSectionWithCertifierDetails}`}>
          <User user={UserMapper.mapOne(certDetails.certifier)} label={Messages.datasourceCardCertifier()}
                urls={props.urls}/>
          <MarkupField label={Messages.dataSourceCertification_notes()} markup={certDetails.certificationNote}/>
        </div>
      )}
    </Tooltip>
);
}

function getRelatedConnectionLinkProps(datasource: IContentInfo, urls: SpacesSiteUrls): EntityLinkProps[] {
  if (datasource && datasource.hasOwnProperty('connections')) {
    const connections: IDataConnectionInfo[] = (datasource as any).connections;
    return connections.map(c => {
      const displayMetadata = ConnectionDisplay.getDisplayMetadata(c, urls);
      const translatedText = ConnectionDisplay.translatedText(displayMetadata);
      return {
        name: translatedText.connectionName,
        href: displayMetadata.href,
        iconImagePath: displayMetadata.iconImageUrl
      };
    });
  } else {
    return [];
  }
}
